import { unique } from "remeda";

import {
  type TPageProduct,
  useProductById,
  useSelectedPageProducts,
  useUpdatePageProducts,
  useSavedColours,
  useUpdateGridSettings,
} from "../../../../data";
import type { TPageFormatting } from "../../../../data/pages";
import { columnWidth as defaultColumnWidthValue } from "../../../globals";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { columnSelectionInfo, selectionColumnIds } from "../SelectionInfo";
import { useUpdateFormatting } from "./useUpdateFormatting";
import { useAddAction } from "../ModalContainer";
import { Toggle, Button, Dropdown } from "../../../ui";
import { Tooltip, TooltipTrigger } from "@artis/ui";
import type { TFormatTypes } from "../../cell-style/helpers";
import { ColorPicker } from "../../../grid-settings/ColorPicker";
import { useClearFormatting } from "../../../menu-system/configs/sections/format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "mcn";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

const volumeAndOiSelectorsForThousandsSeparator = new Set(["oi", "oichg", "volm", "volmprior", "bidsize", "asksize"]);

const decimalPlaceOptions = Array.from({ length: 10 }, (_, i) => ({
  value: i.toString(),
  label: i.toString(),
}));

const getSelectionLabel = (type: TFormatTypes) => {
  switch (type) {
    case "column":
      return "Selected column(s)";
    case "range":
      return "Selected range";
    case "period":
      return "Selected period(s)";
    default:
      return "Selection";
  }
};

const getSelectionInfo = (type: TFormatTypes, selection: TRangeSelection) => {
  switch (type) {
    case "range":
      // Format: "BRT SW - ICE GO: H26-J26" (first-last column: first-last period)
      return selection
        .map((range) => {
          const firstPeriod = range[0]?.rowId;
          const lastPeriod = range[range.length - 1]?.rowId;
          const packageNames = unique(range.map((cell) => cell.headerName));
          const columnRange =
            packageNames.length === 1
              ? packageNames[0]
              : `${packageNames[0]} - ${packageNames[packageNames.length - 1]}`;
          const periodRange = firstPeriod === lastPeriod ? firstPeriod : `${firstPeriod}-${lastPeriod}`;
          return `${columnRange}: ${periodRange}`;
        })
        .join(", ");
    case "period":
      // Format: "H26-J26"
      return selection
        .map((range) => {
          const firstPeriod = range[0]?.rowId;
          const lastPeriod = range[range.length - 1]?.rowId;
          return firstPeriod === lastPeriod ? firstPeriod : `${firstPeriod}-${lastPeriod}`;
        })
        .join(", ");
    default:
      return columnSelectionInfo(selection);
  }
};

export const FormatColumn = ({
  selection,
  pageId,
  type,
  onClose,
}: {
  selection: TRangeSelection;
  pageId: string;
  type: TFormatTypes;
  onClose: () => void;
}) => {
  const [formatting, onFormattingChange] = useUpdateFormatting({ selection, pageId, type });
  const updatePageProducts = useUpdatePageProducts();
  const productIds = selectionColumnIds(selection);
  const pageProducts = useSelectedPageProducts(pageId, productIds);
  const firstPageProduct = pageProducts.data?.[0];
  const productInfo = useProductById(firstPageProduct?.product_id ?? "");
  const actions = useAddAction();
  const clear = useClearFormatting();

  const savedColours = useSavedColours();
  const updateGridSettings = useUpdateGridSettings();

  const isColumnType = type === "column";

  const isCannedOrSourced = productInfo.data?.artis_type === "canned" || productInfo.data?.artis_type === "sourced";
  const isFpOrTs = productInfo.data?.maturity === "future" || productInfo.data?.maturity === "inter_month_spreads";
  const isVolumeOrOiSelector =
    !!firstPageProduct?.column_field_selector &&
    volumeAndOiSelectorsForThousandsSeparator.has(firstPageProduct.column_field_selector);
  const runtimeOverrideActive = isCannedOrSourced && isFpOrTs && isVolumeOrOiSelector;

  const decimalPlacesFromDb = firstPageProduct?.decimal_places;
  const boundedDecimalPlaces =
    typeof decimalPlacesFromDb === "number" ? Math.max(0, Math.min(9, decimalPlacesFromDb)) : null;
  const effectiveDecimalPlaces = runtimeOverrideActive ? 0 : boundedDecimalPlaces;

  const thousandsSeparatorFromDb = !!firstPageProduct?.thousands_separator;
  const effectiveThousandsSeparator = runtimeOverrideActive ? true : thousandsSeparatorFromDb;

  const updatePage = (arg: Partial<TPageProduct>) => {
    if (!actions.hasCancel()) {
      const initial = (pageProducts.data ?? []).map((p) => ({
        id: p.id,
        column_width: p.column_width,
        decimal_places: p.decimal_places,
        thousands_separator: p.thousands_separator,
      }));
      actions.onCancel(() => Promise.all(initial.map(updatePageProducts.optimistic)));
    }
    productIds.forEach((id) => updatePageProducts.optimistic({ id, ...arg }));
  };

  const onUpdateFormatting = (value: Partial<TPageFormatting[string]>) => {
    if (!actions.hasCancel()) {
      const initial = { ...formatting };
      actions.onCancel(() => onFormattingChange(initial));
    }
    onFormattingChange(value);
  };

  const resetColumnFormatting = () => {
    clear({
      type: "column",
      colIds: pageProducts.data?.map((p) => p.id),
    });
  };

  const currentColor = formatting.color ?? "";
  const colorPickerValue = currentColor === "#ffffff00" ? "inherit" : currentColor;

  const handleColorChange = (color: string) => {
    const newColor = color === "inherit" ? "#ffffff00" : color;
    onUpdateFormatting({ color: newColor });
  };

  return (
    <div className="flex flex-col w-full min-w-0 max-w-full px-[var(--spacing-scale-medium-500,15px)] pb-[var(--spacing-scale-medium-500,15px)] gap-[var(--spacing-scale-small-400,12px)]">
      <span className="text-foreground-muted text-[length:var(--font-size-sm)] not-italic font-[var(--font-weights-medium)] leading-[var(--line-height-text-sm)] break-words">
        {getSelectionLabel(type)}: {getSelectionInfo(type, selection)}
      </span>

      <div className="flex flex-col gap-[var(--spacing-scale-small-300,9px)]">
        <Section title="Text">
          {isColumnType && (
            <>
              <SettingRow
                label="Thousands separators"
                control={
                  <TooltipTrigger>
                    <div>
                      <Toggle
                        checked={effectiveThousandsSeparator}
                        disabled={runtimeOverrideActive}
                        onChange={() =>
                          updatePage({
                            thousands_separator: !firstPageProduct?.thousands_separator,
                          })
                        }
                      />
                    </div>
                    <Tooltip>
                      {runtimeOverrideActive
                        ? "Thousands separator is controlled automatically for this field"
                        : undefined}
                    </Tooltip>
                  </TooltipTrigger>
                }
              />
              <SettingRow
                label="Decimal places"
                description="Customise the number of decimal places to display"
                tooltip="​Note: values applied at a column level (through Format column), will supersede values set at a Curve level (through Curve settings)"
                control={
                  <TooltipTrigger>
                    <div data-testid="format-column-decimal-places">
                      <Dropdown
                        className="w-[60px] [&>button>span]:grow [&>button>span]:text-right [&>button>span]:!pr-8"
                        maxWidth="60px"
                        height="25px"
                        value={effectiveDecimalPlaces === null ? undefined : effectiveDecimalPlaces.toString()}
                        placeholder="n/a"
                        options={decimalPlaceOptions}
                        disabled={runtimeOverrideActive}
                        clearable={!runtimeOverrideActive}
                        onClear={() => updatePage({ decimal_places: null })}
                        onChange={(newValue) => {
                          updatePage({ decimal_places: Number.parseInt(newValue, 10) });
                        }}
                      />
                    </div>
                    <Tooltip>
                      {runtimeOverrideActive ? "Decimal places are controlled automatically for this field" : undefined}
                    </Tooltip>
                  </TooltipTrigger>
                }
              />
            </>
          )}
          <SettingRow
            label="Bold text"
            control={
              <Toggle
                checked={!!formatting.boldText}
                onChange={() => onUpdateFormatting({ boldText: !formatting.boldText })}
              />
            }
          />
        </Section>

        <Section title="Colour">
          <SettingRow
            label="Fill colour"
            description={`Set a background colour for this ${isColumnType ? "column" : type === "period" ? "period" : "range"}`}
            control={
              <ColorPicker
                value={colorPickerValue}
                onChange={handleColorChange}
                position="bottom"
                savedColours={savedColours}
                onSavedColoursChange={(colours) => updateGridSettings.optimistic({ saved_colours: colours })}
              />
            }
          />
          <SettingRow
            label="Invert text colour"
            description="Can improve visibility on highlighted cell(s)"
            control={
              <Toggle
                checked={!!formatting.invertTextColor}
                onChange={() => onUpdateFormatting({ invertTextColor: !formatting.invertTextColor })}
              />
            }
          />
        </Section>

        {isColumnType && (
          <Section title="Column width">
            <SettingRow
              label="Reset column width to default"
              control={
                <Button
                  buttonType="linkButton"
                  onClick={() => {
                    updatePage({
                      column_width: defaultColumnWidthValue,
                    });
                  }}
                >
                  Reset
                </Button>
              }
            />
          </Section>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Button buttonType="linkButton" onClick={resetColumnFormatting}>
          Reset Formatting
        </Button>
        <Button buttonType="closePrimary" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-[3px]">
    <span className="text-foreground text-[12px] not-italic font-[var(--font-weights-medium)] leading-[var(--line-height-text-base)]">
      {title}
    </span>
    <div className="flex flex-col gap-[6px] px-[var(--spacing-scale-small-200,6px)]">{children}</div>
  </div>
);

const SettingRow = ({
  label,
  description,
  tooltip,
  control,
}: {
  label: string;
  description?: string;
  tooltip?: string;
  control: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-[9px]">
    <div className="flex flex-col gap-px flex-1 min-w-0">
      <span className="text-foreground text-[length:var(--font-size-base)] not-italic font-[var(--font-weights-normal)] leading-[var(--line-height-text-base)]">
        {label}
        {tooltip && (
          <TooltipTrigger as="span" touch="toggle" focusable>
            <span className={cn("px-1.5 py-0.5 rounded text-[12px]")}>
              <FontAwesomeIcon icon={faCircleInfo} className="text-neutral" />
            </span>
            <Tooltip>{tooltip}</Tooltip>
          </TooltipTrigger>
        )}
      </span>
      {description && (
        <span className="text-foreground-muted text-[length:var(--font-size-sm)] not-italic font-[var(--font-weights-medium)] leading-[var(--line-height-text-sm)]">
          {description}
        </span>
      )}
    </div>
    <div className="flex-shrink-0">{control}</div>
  </div>
);
